import { OnboardingScreen } from './screens';

/**
 * Plain shape consumed by `deriveOnboardingScreen`. Decoupled from generated
 * Apollo types so the function is trivially unit-testable and survives query
 * shape changes — the hook adapts its `useQuery` result into this shape.
 *
 * `dependants` are derived from the current user's `CaregiverRelationship`
 * rows in the hook (not fetched from `family.dependants` directly). The
 * `createDependantAndRelationship` mutation creates the dependant + caregiver
 * link atomically, so every dependant the user knows about is by definition
 * already linked — querying both lists would be redundant and creates a
 * "dependant missing user link" check that's impossible to fail in practice.
 */
export interface IOnboardingProxyData {
  user: {
    id: string;
    phoneNumber: string | null;
  };
  /**
   * `true` when the user has at least one residence row flagged
   * `isPrimary`. The wizard requires a primary address before advancing —
   * see the gating rule in {@link deriveOnboardingScreen}.
   */
  hasPrimaryResidence: boolean;
  dependants: ReadonlyArray<{ id: string }>;
  dependantPartnerships: ReadonlyArray<{
    dependants: ReadonlyArray<{ id: string }>;
  }>;
  /** `true` when any of the user's dependants has an email or phone saved. */
  hasDependantContactInfo: boolean;
  /**
   * `true` when the loved-ones residence step has been answered — any
   * `livesWithCaregiver` or partnership `liveTogether` flag is non-null.
   * Relies on `DependantResidenceStep`'s `isFormReady` invariant: Continue is
   * blocked until the "live with you?" answer is non-null, so a completed
   * (not skipped) step always writes one of these. If that form rule is ever
   * relaxed this signal must be hardened, or users get stuck on residence.
   */
  hasDependantResidenceInfo: boolean;
  vehicleCount: number;
  financialAccountCount: number;
}

/**
 * Derive the next onboarding screen for a partially-onboarded user, or
 * `null` if the user is fully onboarded.
 *
 * Gating rules (checked in order; the first unmet rule wins):
 *   1. Phone number entered (`phoneNumber` non-null) → otherwise
 *      `PHONE_NUMBER`. Fresh users land here naturally. We deliberately
 *      do NOT gate on `phoneNumberVerifiedAt`: `PHONE_NUMBER_VERIFY` is a
 *      forward-only screen in the wizard sequence (like `GREAT_START` and
 *      `ALMOST_THERE`) and never a derive target — verification is optional,
 *      and a user who entered their phone but didn't verify resumes at the
 *      next data gate.
 *   2. Primary residence saved (`hasPrimaryResidence`) → otherwise
 *      `USER_RESIDENCE`. The proxy query reads `user.primaryResidence` —
 *      its presence is the only signal the resume derivation has that this
 *      step has been completed.
 *   3. At least one dependant linked to the current user → otherwise
 *      `DEPENDANT_COUNT`. The count-picker is the entry point to the
 *      loved-ones phase; once a dependant exists, the count step is
 *      implicitly satisfied. The wizard's linear order takes the user from
 *      `DEPENDANT_COUNT` → `DEPENDANT_ENTRY` → `DEPENDANT_REVIEW`.
 *   4. When the user has exactly two dependants, they're linked to each
 *      other via a `DependantPartnership` → otherwise `DEPENDANT_COUNT`.
 *      The partnership link is fired on the review step; users land back
 *      at the count picker (and forward through entry → review) until both
 *      dependants are connected. Users with 3+ dependants don't currently
 *      have a pair-link requirement (the product rule is "if there are
 *      two, link them"); revisit if the wizard adds multi-partner support.
 *   5. The optional loved-ones tail — `DEPENDANT_CONTACT`, `DEPENDANT_RESIDENCE`,
 *      `DEPENDANT_VEHICLE` — followed by the `FINANCIAL_ACCT_CONNECT` gate, is
 *      resolved by **frontier resume** rather than first-unmet. Each of these
 *      screens can be skipped (the "Skip for now" button writes nothing), so
 *      data presence can't distinguish "skipped" from "never reached." We find
 *      the furthest screen in the tail that has data and resume at the next one:
 *      a screen counts as satisfied if it has its own data OR any later screen
 *      does. This never fast-forwards past an unfilled screen, and once the user
 *      has progressed past a skipped screen they aren't yanked back to it.
 *   6. Tail fully satisfied (financial account connected) → `null` (onboarded).
 *
 * `DEPENDANT_REVIEW`, `DEPENDANT_ENTRY`, and `PAYMENT` carry no completion
 * signal of their own (review/entry are covered by the dependant-count gate;
 * `PAYMENT` is a transitional screen), so they are never derive targets. The
 * frontier collapse below assumes the tail is contiguous and financial is last —
 * revisit if `PAYMENT` ever becomes a data-bearing gate.
 */
export const deriveOnboardingScreen = (data: IOnboardingProxyData): OnboardingScreen | null => {
  const {
    user,
    hasPrimaryResidence,
    dependants,
    dependantPartnerships,
    hasDependantContactInfo,
    hasDependantResidenceInfo,
    vehicleCount,
    financialAccountCount,
  } = data;

  if (user.phoneNumber === null) {
    return OnboardingScreen.PHONE_NUMBER;
  }
  if (!hasPrimaryResidence) {
    return OnboardingScreen.USER_RESIDENCE;
  }
  if (dependants.length === 0) {
    return OnboardingScreen.DEPENDANT_COUNT;
  }

  // Two-dependant pair-link requirement. `DependantPartnership.dependants` is
  // a variable-length array on the wire; for the two-dependant rule, we just
  // need a partnership whose dependants set contains both of the user's
  // dependants.
  if (dependants.length === 2) {
    const [a, b] = dependants;
    const linked = dependantPartnerships.some((p) => {
      const ids = new Set(p.dependants.map((d) => d.id));
      return ids.has(a.id) && ids.has(b.id);
    });
    if (!linked) {
      return OnboardingScreen.DEPENDANT_COUNT;
    }
  }

  // Frontier resume over the optional tail. Resume at the screen just past the
  // furthest one that has data; if that's the financial slot and no account is
  // connected, resume there; if even financial has data, the user is onboarded.
  const tail: ReadonlyArray<{ screen: OnboardingScreen; hasData: boolean }> = [
    { screen: OnboardingScreen.DEPENDANT_CONTACT, hasData: hasDependantContactInfo },
    { screen: OnboardingScreen.DEPENDANT_RESIDENCE, hasData: hasDependantResidenceInfo },
    { screen: OnboardingScreen.DEPENDANT_VEHICLE, hasData: vehicleCount > 0 },
    { screen: OnboardingScreen.FINANCIAL_ACCT_CONNECT, hasData: financialAccountCount > 0 },
  ];
  const lastDataIdx = tail.reduce((acc, entry, i) => (entry.hasData ? i : acc), -1);

  return lastDataIdx === tail.length - 1 ? null : tail[lastDataIdx + 1].screen;
};
